import { RefObject, useEffect, useState } from 'react';

/**
 * Use ResizeObserver to listen for the dimensions changes of an element.
 * @param elRef
 * @param debounceTime
 */
export function useResize(elRef: RefObject<Element>, debounceTime = 0) {
    const [dimensions, setDimensions] = useState<Omit<DOMRectReadOnly, 'toJSON'>>({
        width: 0,
        height: 0,
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        x: 0,
        y: 0
    });

    useEffect(() => {
        const el = elRef.current;
        if (!el) return;

        let callbackTimerId = 0;
        const resizeObserver = new ResizeObserver((entries) => {
            const callback = () => setDimensions(entries[0].contentRect);
            if (debounceTime) {
                if (callbackTimerId) {
                    window.clearTimeout(callbackTimerId);
                }
                callbackTimerId = window.setTimeout(callback, debounceTime);
            } else {
                callback();
            }
        });
        resizeObserver.observe(el);

        return () => {
            if (callbackTimerId) {
                window.clearTimeout(callbackTimerId);
            }
            resizeObserver.unobserve(el);
        };
    }, [elRef, debounceTime]);

    return dimensions;
}
